import React from 'react';
import { useRedirect, useDataProvider } from 'react-admin';
import { PlayCircle, ChevronRight, X as XIcon, Calendar } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, PieChart, Pie, Cell } from 'recharts';
import { ChartContainer, ChartTooltip, ChartTooltipContent, type ChartConfig } from './components/ui/simple-chart';
import { StatCard, StatRow } from './components/ui/stat-card';
import { Avatar, StatusBadge } from './components/ui/data-cells';
import { toastSuccess, toastError } from './lib/toast';
import { cn } from './lib/utils';
import { useDashboard } from './contexts/DashboardContext';
import { CustomDataProvider } from './types/dataProvider';
import { logger, LogCategory } from './utils/logger';

/* ============================================================
   Overview (Dashboard home) — Latio design system (DESIGN_SYSTEM.md §6).
   KPI StatCards → pending coach applications + top sports → ongoing sessions +
   capacity → registration trend. Real backend data preserved throughout.
   ============================================================ */

const INDIGO = '#4361AD';
const INDIGO_TINT = '#E7EAF5';

function DashCard({
  title,
  action,
  onAction,
  children,
  className,
}: {
  title: string;
  action?: string;
  onAction?: () => void;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div className={cn('rounded-card border border-hairline bg-background p-5', className)}>
      <div className="mb-4 flex items-center justify-between gap-3">
        <h3 className="text-[15px] font-bold text-ink">{title}</h3>
        {action && (
          <button
            type="button"
            onClick={onAction}
            className="inline-flex items-center gap-1 text-[13px] font-semibold text-indigo hover:text-indigo-press"
          >
            {action}
            <ChevronRight className="size-3.5" />
          </button>
        )}
      </div>
      {children}
    </div>
  );
}

/* ---- Pending coach applications (action list) ---- */
function PendingApplications({ apps, onChanged }: { apps: any[]; onChanged: () => void }) {
  const redirect = useRedirect();
  const dataProvider = useDataProvider<CustomDataProvider>();
  const [busy, setBusy] = React.useState<number | null>(null);

  const approve = async (app: any) => {
    setBusy(app.id);
    try {
      await dataProvider.update('trainer-applications', { id: app.id, data: { approved: true } as any, previousData: app });
      toastSuccess(`${app.user?.name || 'Applicant'} approved`);
      onChanged();
    } catch {
      toastError('Failed to approve application');
    } finally {
      setBusy(null);
    }
  };

  if (apps.length === 0) {
    return <p className="py-8 text-center text-sm text-ink-2">No pending applications 🎉</p>;
  }

  return (
    <div className="flex flex-col">
      {apps.map((a, i) => (
        <div
          key={a.id}
          onClick={() => redirect('show', 'trainer-applications', a.id)}
          className={cn('flex cursor-pointer items-center gap-3 py-3', i > 0 && 'border-t border-hairline')}
        >
          <Avatar src={a.user?.profile_picture_url} name={a.user?.name} size={38} />
          <div className="min-w-0 flex-1">
            <div className="truncate text-sm font-semibold text-ink">{a.user?.name || `Application #${a.id}`}</div>
            <div className="truncate text-[12.5px] text-ink-3">{a.user?.email || a.user?.username || '—'}</div>
          </div>
          <span onClick={(e) => e.stopPropagation()} className="inline-flex gap-2">
            <button
              type="button"
              onClick={() => approve(a)}
              disabled={busy === a.id}
              className="inline-flex h-8 items-center rounded-pill bg-black px-3 text-[13px] font-semibold text-white hover:bg-ink disabled:opacity-50"
            >
              Approve
            </button>
            <button
              type="button"
              onClick={() => redirect('show', 'trainer-applications', a.id)}
              title="Review / reject"
              className="inline-flex size-8 items-center justify-center rounded-pill border border-hairline text-ink-2 hover:bg-surface-2"
            >
              <XIcon className="size-4" />
            </button>
          </span>
        </div>
      ))}
    </div>
  );
}

/* ---- Top sports bars ---- */
function TopSports({ data }: { data: any[] }) {
  const rows = (data || [])
    .map((d) => ({ sport: String(d.sport || d.sport_name || '—'), count: Number(d.count ?? d.session_count ?? 0) }))
    .sort((a, b) => b.count - a.count)
    .slice(0, 6);
  const max = rows.length > 0 ? Math.max(...rows.map((r) => r.count), 1) : 1;
  if (rows.length === 0) return <p className="py-8 text-center text-sm text-ink-2">No sport data</p>;
  return (
    <div className="flex flex-col gap-3.5">
      {rows.map((s) => (
        <div key={s.sport} className="flex items-center gap-3">
          <span className="w-16 shrink-0 truncate text-[13.5px] font-medium capitalize text-ink">{s.sport}</span>
          <div className="h-2 flex-1 overflow-hidden rounded-pill bg-surface-2">
            <div className="h-full rounded-pill bg-indigo" style={{ width: `${(s.count / max) * 100}%` }} />
          </div>
          <span className="w-9 shrink-0 text-right text-[13px] tabular-nums text-ink-2">{s.count}</span>
        </div>
      ))}
    </div>
  );
}

/* ---- Ongoing sessions ---- */
function OngoingSessions({ sessions }: { sessions: any[] }) {
  const redirect = useRedirect();
  const ongoing = (sessions || []).filter((s) => s.status === 'active' || s.status === 'scheduled' || s.status === 'open');
  if (ongoing.length === 0) {
    return (
      <div className="py-10 text-center text-ink-3">
        <PlayCircle className="mx-auto mb-2 size-10 text-hairline" />
        <p className="text-sm">No ongoing sessions right now</p>
      </div>
    );
  }
  return (
    <div className="flex flex-col">
      {ongoing.slice(0, 6).map((s, i) => (
        <div
          key={s.id}
          onClick={() => redirect('show', 'sessions', s.id)}
          className={cn('flex cursor-pointer items-center gap-3 py-3', i > 0 && 'border-t border-hairline')}
        >
          <span className="flex size-9 items-center justify-center rounded-latio-sm bg-indigo-tint text-indigo">
            <Calendar className="size-4" />
          </span>
          <div className="min-w-0 flex-1">
            <div className="truncate text-sm font-semibold text-ink">{s.title || `Session #${s.id}`}</div>
            <div className="truncate text-[12.5px] text-ink-3">
              {s.start_time ? new Date(s.start_time).toLocaleString() : 'Schedule TBD'} · {s.current_participants || 0}/{s.max_participants || 0}
            </div>
          </div>
          <StatusBadge status={s.status} />
        </div>
      ))}
    </div>
  );
}

/* ---- Charts ---- */
function CapacityChart({ stats }: { stats: any }) {
  const full = stats?.full_capacity_sessions || 0;
  const total = stats?.completed_ongoing_sessions || 0;
  const partial = Math.max(0, total - full);
  const pct = total > 0 ? ((full / total) * 100).toFixed(0) : '0';
  const data = [
    { name: 'Full', value: full, fill: INDIGO },
    { name: 'Partial', value: partial, fill: INDIGO_TINT },
  ];
  const config = { value: { label: 'Sessions', color: INDIGO } } satisfies ChartConfig;
  return (
    <DashCard title={`Capacity fulfillment · ${pct}%`}>
      <ChartContainer config={config} className="h-[260px]">
        <PieChart>
          <Pie data={data} cx="50%" cy="50%" innerRadius={55} outerRadius={85} paddingAngle={2} dataKey="value">
            {data.map((d, i) => (
              <Cell key={i} fill={d.fill} stroke="none" />
            ))}
          </Pie>
          <ChartTooltip content={<ChartTooltipContent />} />
        </PieChart>
      </ChartContainer>
      <div className="mt-2 flex items-center justify-center gap-5 text-[13px]">
        <span className="inline-flex items-center gap-1.5 text-ink-2"><span className="size-2.5 rounded-full bg-indigo" />Full ({full})</span>
        <span className="inline-flex items-center gap-1.5 text-ink-2"><span className="size-2.5 rounded-full bg-indigo-tint" />Partial ({partial})</span>
      </div>
    </DashCard>
  );
}

function RegistrationChart({ data }: { data: any[] }) {
  const chartData = (data || []).map((item) => ({
    date: new Date(item.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
    users: item.count,
  }));
  const config = { users: { label: 'New users', color: INDIGO } } satisfies ChartConfig;
  return (
    <DashCard title="New user registrations · 30 days">
      <ChartContainer config={config} className="h-[280px] w-full">
        <LineChart data={chartData} margin={{ left: -16, right: 8, top: 8 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#E0E0E0" vertical={false} />
          <XAxis dataKey="date" tickLine={false} axisLine={false} tick={{ fontSize: 12, fill: '#757575' }} />
          <YAxis tickLine={false} axisLine={false} tick={{ fontSize: 12, fill: '#757575' }} />
          <ChartTooltip content={<ChartTooltipContent />} />
          <Line type="monotone" dataKey="users" stroke={INDIGO} strokeWidth={2} dot={false} activeDot={{ r: 4, fill: INDIGO }} />
        </LineChart>
      </ChartContainer>
    </DashCard>
  );
}

/* ============================================================
   Main
   ============================================================ */
export const Dashboard: React.FC = () => {
  const { setRefreshFunction, setIsRefreshing } = useDashboard();
  const dataProvider = useDataProvider<CustomDataProvider>();

  const [stats, setStats] = React.useState<any>(null);
  const [analytics, setAnalytics] = React.useState<any>(null);
  const [sessions, setSessions] = React.useState<any[]>([]);
  const [chatStats, setChatStats] = React.useState<any>(null);
  const [trainerStats, setTrainerStats] = React.useState<any>(null);
  const [pendingApps, setPendingApps] = React.useState<any[]>([]);
  const [loading, setLoading] = React.useState(true);

  const fetchDashboardData = React.useCallback(async () => {
    try {
      setLoading(true);
      const [statsResult, analyticsResult, sessionsResult, chatResult, trainerResult] = await Promise.allSettled([
        dataProvider.getDashboardStats(),
        dataProvider.getPlatformAnalytics({ days: 30 }),
        dataProvider.getRecentSessions({ limit: 10 }),
        dataProvider.getChatStats(),
        dataProvider.getList('trainer-applications', {
          pagination: { page: 1, perPage: 1000 },
          sort: { field: 'created_at', order: 'DESC' },
          filter: {},
        }),
      ]);

      if (statsResult.status === 'fulfilled') setStats(statsResult.value.data);
      if (analyticsResult.status === 'fulfilled') setAnalytics(analyticsResult.value.data);
      if (sessionsResult.status === 'fulfilled') setSessions(sessionsResult.value.data);
      if (chatResult.status === 'fulfilled') setChatStats(chatResult.value.data);

      if (trainerResult.status === 'fulfilled') {
        const apps = trainerResult.value.data || [];
        const pending = apps.filter((a: any) => a.status === 'PENDING');
        setPendingApps(pending.slice(0, 4));
        setTrainerStats({
          pending_applications: pending.length,
          total_trainers: apps.filter((a: any) => a.status === 'APPROVED').length,
          total_applications: apps.length,
        });
      } else {
        setPendingApps([]);
        setTrainerStats({ pending_applications: 0, total_trainers: 0, total_applications: 0 });
      }
    } catch (error) {
      logger.error(LogCategory.API, 'Error fetching dashboard data', error, 'Dashboard');
    } finally {
      setLoading(false);
      setIsRefreshing(false);
    }
  }, [dataProvider, setIsRefreshing]);

  React.useEffect(() => {
    setRefreshFunction(fetchDashboardData);
    fetchDashboardData();
  }, [fetchDashboardData, setRefreshFunction]);

  const redirect = useRedirect();

  if (loading || !stats) {
    return <div className="p-6 text-center text-ink-2">Loading overview…</div>;
  }

  const num = (v: any) => (typeof v === 'number' ? v.toLocaleString('de-DE') : v ?? 0);

  return (
    <div className="space-y-6 p-6">
      {/* KPIs */}
      <StatRow>
        <StatCard
          primary
          label="Active coaches"
          value={num(trainerStats?.total_trainers)}
          delta={trainerStats?.pending_applications ? { value: `${trainerStats.pending_applications} pending`, up: true } : undefined}
        />
        <StatCard
          label="Athletes"
          value={num(stats.total_users)}
          delta={stats.new_users_week ? { value: `+${stats.new_users_week}`, up: true } : undefined}
        />
        <StatCard
          label="Total sessions"
          value={num(stats.total_sessions)}
          delta={stats.sessions_today ? { value: `${stats.sessions_today} today`, up: true } : undefined}
        />
        <StatCard label="Reviews" value={num(stats.total_reviews)} />
      </StatRow>

      <StatRow>
        <StatCard label="Pending applications" value={num(trainerStats?.pending_applications)} />
        <StatCard label="Active users today" value={num(stats.active_users_today)} />
        <StatCard
          label="Chat messages"
          value={num(chatStats?.total_messages)}
          delta={chatStats?.messages_today ? { value: `${chatStats.messages_today} today`, up: true } : undefined}
        />
        <StatCard label="Active chats" value={num(chatStats?.active_chats)} />
      </StatRow>

      {/* Applications + top sports */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-[1.5fr_1fr]">
        <DashCard title="Pending coach applications" action="Review all" onAction={() => redirect('list', 'trainer-applications')}>
          <PendingApplications apps={pendingApps} onChanged={fetchDashboardData} />
        </DashCard>
        <DashCard title="Top sports this week">
          <TopSports data={analytics?.sport_popularity} />
        </DashCard>
      </div>

      {/* Ongoing + capacity */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <DashCard title="Ongoing sessions">
          <OngoingSessions sessions={sessions} />
        </DashCard>
        {stats && <CapacityChart stats={stats} />}
      </div>

      {/* Registration trend */}
      {analytics?.user_registration_trends && <RegistrationChart data={analytics.user_registration_trends} />}
    </div>
  );
};

export default Dashboard;
